import { uploadQueue } from '../services/uploadQueue';
import { PlaygroundFeatureAccessProvider } from '../contexts/FeatureAccessContext';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Film,
  Play,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react';
import {
  apiService,
  getApiErrorMessage,
  Prompt,
  PromptRun,
  SearchResult,
  Video,
} from '../services/api';
import {
  generateIdempotencyKey,
  isRetryableStreamRequestError,
} from '../services/idempotency';
import {
  DEFAULT_PLAYGROUND_VIEW_STATE,
  PlaygroundAttemptIdentity,
  PlaygroundJourneyStep,
  PlaygroundViewState,
  useTabState,
} from '../contexts/TabStateContext';
import { useAuth } from '../contexts/AuthContext';
import { parseResultLink, RESULT_VIEW_KEYS, ResultLinkTarget, resultViewParams } from '../navigation/resultLinks';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { useEngineCatalog } from '../hooks/useEngineCatalog';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { segmentCache } from '../services/segmentCache';
import {
  applyRealtimeStateToVideo,
  processingRealtimeStore,
} from '../services/processingRealtimeStore';
import { isInProgressStatus, isVideoInProgress } from '../utils/realtimeStreamEligibility';
import { describeInsufficientCredits, extractInsufficientCredits } from '../utils/billingErrors';
import { extractPlanUpgradeRequired } from '../utils/planUpgrade';
import {
  createErrorRecoveryAction,
  ErrorRecoveryAction,
  getVisibleErrorRecoveryAction,
} from '../utils/errorRecoveryAction';
import { useVideoDeletionPolling } from '../hooks/useVideoDeletionPolling';
import {
  excludeAcceptedVideoDeletions,
  PendingVideoDeletionMap,
  recordAcceptedVideoDeletion,
} from '../utils/videoDeletionState';
import {
  applyVideoAssetsReadyEvent,
  ensureDurableVideoPreviewFields,
} from '../utils/videoPreviewProjection';
import {
  createPlaygroundStudioWorkspaceState,
  PlaygroundStudioWorkspaceState,
} from '../features/playground/studioWorkspaceState';
import {
  exceedsMediaUploadSizeLimit,
  isSupportedLocalMediaFile,
  LOCAL_MEDIA_FILE_ACCEPT,
  MEDIA_UPLOAD_SIZE_LIMIT_LABEL,
  MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE,
} from '../utils/mediaUploadPolicy';
import EngineGallery from './EngineGallery';
import VideoTile from './VideoTile';
import { DeferredPreviewTile, PreviewViewportProvider } from './GrantedPreviewImage';
import VideoDetailModal from './VideoDetailModal';
import StudioSearchWorkspace, { StudioSearchResultCarousel } from './studio-search/StudioSearchWorkspace';
import StudioVideoRail from './studio-search/StudioVideoRail';
import { createWebStudioSearchTransport } from '../features/studio-search/webTransport';
import type { StudioSearchState } from '../features/studio-search/types';
import { getSearchResultKey, getSearchResultRunId } from '../utils/searchResultIdentity';
import { preserveScrollPosition } from '../utils/preserveScrollPosition';
import './PlaygroundTab.css';

type JourneyStep = PlaygroundJourneyStep;

const OWNED_KEYS = ['view', 'step', 'video', 'run', 'runPrompt', 'vq', 'vsort', 'vdir', ...RESULT_VIEW_KEYS];
const VIDEOS_PAGE_SIZE = 50;

const mergeVideos = (primary: Video[], secondary: Video[]): Video[] => {
  const seen = new Set<string>();
  return [...primary, ...secondary].filter((video) => {
    if (!video?.video_id || seen.has(video.video_id)) return false;
    seen.add(video.video_id);
    return true;
  });
};

const formatMediaSummary = (selectedMedia: Video[]): string => {
  if (selectedMedia.length === 0) return 'Upload media or choose from your library';
  if (selectedMedia.length === 1) return '1 media item selected';
  return `${selectedMedia.length} media items selected`;
};

const JourneySection: React.FC<{
  step: JourneyStep;
  number: number;
  title: string;
  summary: string;
  activeStep: JourneyStep | null;
  complete: boolean;
  collapsedContent?: React.ReactNode;
  headerActionsRef?: React.Ref<HTMLDivElement>;
  onActivate: (step: JourneyStep) => void;
  children: React.ReactNode;
}> = ({ step, number, title, summary, activeStep, complete, collapsedContent, headerActionsRef, onActivate, children }) => {
  const isActive = activeStep === step;
  const contentId = `playground-step-${step}`;
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive) bodyRef.current?.removeAttribute('inert');
    else bodyRef.current?.setAttribute('inert', '');
  }, [isActive]);

  return (
    <section className={`playground-journey-step playground-journey-step--${step} ${isActive ? 'is-active' : ''} ${complete ? 'is-complete' : ''} ${!isActive && collapsedContent ? 'has-collapsed-content' : ''} ${isActive && headerActionsRef ? 'has-header-actions' : ''}`}>
      <div
        className="playground-journey-step__header"
        onClick={(event) => {
          if (event.target instanceof Element
            && event.target.closest('button, a, input, select, textarea, [role="button"]')) return;
          onActivate(step);
        }}
      >
        <div
          className="playground-journey-step__header-main"
          role="button"
          tabIndex={0}
          onClick={(event) => {
            if (event.target instanceof Element
              && event.target.closest('button, a, input, select, textarea')) return;
            onActivate(step);
          }}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget
              || (event.key !== 'Enter' && event.key !== ' ')) return;
            event.preventDefault();
            onActivate(step);
          }}
          aria-expanded={isActive}
          aria-controls={contentId}
        >
          <span className="playground-journey-step__number" aria-hidden="true">
            {complete ? <Check size={16} /> : number}
          </span>
          <span className="playground-journey-step__heading">
            <span className="playground-journey-step__title">{title}</span>
            <span className="playground-journey-step__summary">{summary}</span>
          </span>
          {!isActive && collapsedContent && (
            <div className="playground-journey-step__collapsed-content">
              {collapsedContent}
            </div>
          )}
        </div>
        {isActive && headerActionsRef && (
          <div ref={headerActionsRef} className="playground-journey-step__header-actions" />
        )}
        <button
          type="button"
          className="playground-journey-step__toggle"
          onClick={() => onActivate(step)}
          aria-label={`${isActive ? 'Collapse' : 'Expand'} ${title}`}
          aria-expanded={isActive}
          aria-controls={contentId}
        >
          <ChevronRight className="playground-journey-step__chevron" size={18} aria-hidden="true" />
        </button>
      </div>
      <div ref={bodyRef} id={contentId} className="playground-journey-step__body" aria-hidden={!isActive}>
        <div className="playground-journey-step__body-inner">{children}</div>
      </div>
    </section>
  );
};

const PlaygroundTab: React.FC<{ isActive?: boolean }> = ({ isActive = true }) => {
  const { currentUser } = useAuth();
  const authScopeId = currentUser?.uid || null;
  const {
    playgroundTabState,
    setPlaygroundTabState,
    commitPlaygroundViewState,
    playgroundTabStateReady,
  } = useTabState();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    prompts,
    isLoading: isLoadingPrompts,
    error: engineCatalogError,
  } = useEngineCatalog();

  const acceptedVideoDeletionIdsRef = useRef<Set<string>>(
    new Set(Object.keys(playgroundTabState.pendingVideoDeletions || {})),
  );
  const playgroundVideosRef = useRef<Video[]>(playgroundTabState.videos || []);
  const admittedRunByVideoRef = useRef<Map<string, string>>(new Map(
    Object.entries(playgroundTabState.viewState?.admittedRunByVideoId || {}),
  ));
  const activeStudioVideoIdRef = useRef<string | null>(
    playgroundTabState.viewState?.activeStudioVideoId || null,
  );
  const studioRunIdRef = useRef<string | null>(
    playgroundTabState.viewState?.studioRunId || null,
  );
  const paginationRestoreRef = useRef({
    nextCursor: playgroundTabState.viewState?.videosNextCursor || null,
    hasMore: Boolean(playgroundTabState.viewState?.hasMoreVideos),
    loadedCount: playgroundTabState.viewState?.loadedVideoCount || 0,
  });
  const statusReconcileRequestRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const historyModeRef = useRef<HistoryMode>('replace');
  const lastAppliedStudioRequestRef = useRef<string | null>(null);
  const runAttemptRef = useRef<PlaygroundAttemptIdentity | null>(
    playgroundTabState.viewState?.pendingRunAttempt
      ? { ...playgroundTabState.viewState.pendingRunAttempt }
      : null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const studioResultSpaceRef = useRef<HTMLDivElement>(null);
  const pendingStudioScrollTopRef = useRef<number | null>(null);
  const studioSelectorRef = useRef<HTMLDivElement>(null);
  const scrollPersistTimerRef = useRef<number | null>(null);
  const studioSelectorScrollPersistTimerRef = useRef<number | null>(null);
  const latestScrollTopRef = useRef(0);
  const latestStudioSelectorScrollTopRef = useRef(0);
  const hasRestoredScrollRef = useRef(false);
  const hasRestoredStudioSelectorScrollRef = useRef(false);
  const latestPlaybackTimeRef = useRef(
    playgroundTabState.viewState?.studioPlaybackTime || 0,
  );
  const playbackTimeDirtyRef = useRef(false);
  const playbackRestoreRef = useRef({
    scopeKey: `${playgroundTabState.viewState?.activeStudioVideoId || ''}:${playgroundTabState.viewState?.studioRunId || ''}`,
    time: playgroundTabState.viewState?.studioPlaybackTime || 0,
  });

  const viewState = playgroundTabState.viewState || DEFAULT_PLAYGROUND_VIEW_STATE;
  const setViewStateField = useCallback(<Key extends keyof PlaygroundViewState,>(
    key: Key,
    nextValue: React.SetStateAction<PlaygroundViewState[Key]>,
  ) => {
    setPlaygroundTabState((previous) => {
      const currentViewState = previous.viewState || DEFAULT_PLAYGROUND_VIEW_STATE;
      const currentValue = currentViewState[key];
      const next = typeof nextValue === 'function'
        ? (nextValue as (current: PlaygroundViewState[Key]) => PlaygroundViewState[Key])(currentValue)
        : nextValue;
      if (Object.is(currentValue, next)) return previous;
      return {
        ...previous,
        viewState: { ...currentViewState, [key]: next },
      };
    });
  }, [setPlaygroundTabState]);

  const {
    activeStep,
    activeStudioVideoId,
    studioRunId,
    studioPlaybackTime,
    admittedRunByVideoId,
    pendingRunAttempt,
    videosNextCursor,
    hasMoreVideos,
    studioSelectorScrollTop,
    studioWorkspaceState,
    studioSearchMode = 'studio',
    studioSearchState,
    scrollTop,
  } = viewState;
  const setActiveStep = useCallback((next: React.SetStateAction<JourneyStep | null>) => setViewStateField('activeStep', next), [setViewStateField]);
  const setActiveStudioVideoId = useCallback((next: React.SetStateAction<string | null>) => setViewStateField('activeStudioVideoId', next), [setViewStateField]);
  const setStudioRunId = useCallback((next: React.SetStateAction<string | null>) => setViewStateField('studioRunId', next), [setViewStateField]);
  const setStudioPlaybackTime = useCallback((next: React.SetStateAction<number>) => setViewStateField('studioPlaybackTime', next), [setViewStateField]);
  const resetStudioPlaybackPosition = useCallback(() => {
    latestPlaybackTimeRef.current = 0;
    playbackTimeDirtyRef.current = false;
    playbackRestoreRef.current = {
      scopeKey: `${activeStudioVideoIdRef.current || ''}:${studioRunIdRef.current || ''}`,
      time: 0,
    };
    setStudioPlaybackTime(0);
  }, [setStudioPlaybackTime]);
  const setAdmittedRunByVideoId = useCallback((next: React.SetStateAction<Record<string, string>>) => setViewStateField('admittedRunByVideoId', next), [setViewStateField]);
  const setPendingRunAttempt = useCallback((next: React.SetStateAction<PlaygroundViewState['pendingRunAttempt']>) => setViewStateField('pendingRunAttempt', next), [setViewStateField]);
  const setVideosNextCursor = useCallback((next: React.SetStateAction<string | null>) => setViewStateField('videosNextCursor', next), [setViewStateField]);
  const setHasMoreVideos = useCallback((next: React.SetStateAction<boolean>) => setViewStateField('hasMoreVideos', next), [setViewStateField]);
  const setLoadedVideoCount = useCallback((next: React.SetStateAction<number>) => setViewStateField('loadedVideoCount', next), [setViewStateField]);
  const setStudioSelectorScrollTop = useCallback((next: React.SetStateAction<number>) => setViewStateField('studioSelectorScrollTop', next), [setViewStateField]);
  const setStudioWorkspaceState = useCallback((next: React.SetStateAction<PlaygroundStudioWorkspaceState>) => setViewStateField('studioWorkspaceState', next), [setViewStateField]);
  const setStudioSearchMode = useCallback((next: PlaygroundViewState['studioSearchMode']) => setViewStateField('studioSearchMode', next), [setViewStateField]);
  const setStudioSearchState = useCallback((next: StudioSearchState) => setViewStateField('studioSearchState', next), [setViewStateField]);
  const studioSearchTransport = useMemo(() => createWebStudioSearchTransport(), []);
  const [hasOpenedStudioSearch, setHasOpenedStudioSearch] = useState(studioSearchMode !== 'studio');
  const [studioSearchResults, setStudioSearchResults] = useState<SearchResult[]>([]);
  const [studioSearchHit, setStudioSearchHit] = useState<SearchResult | null>(null);
  const [studioSearchVideo, setStudioSearchVideo] = useState<Video | null>(null);
  const [studioSearchVideoError, setStudioSearchVideoError] = useState<string | null>(null);
  const [studioSearchResultLink, setStudioSearchResultLink] = useState<ResultLinkTarget | null>(null);
  const studioSearchSelectionGeneration = useRef(0);
  useEffect(() => {
    if (studioSearchMode !== 'studio') setHasOpenedStudioSearch(true);
  }, [studioSearchMode]);
  const openStudioSearchHit = useCallback((hit: SearchResult, hits?: SearchResult[]) => {
    const generation = ++studioSearchSelectionGeneration.current;
    setStudioSearchHit(hit);
    if (hits) setStudioSearchResults(hits);
    setStudioSearchMode('result');
    setViewStateField('studioSearchState', (previous) => ({ ...previous, selectedResultKey: getSearchResultKey(hit) }));
    setStudioSearchResultLink({
      videoId: hit.video_id, runId: getSearchResultRunId(hit), indexId: null,
      segmentId: hit.segment_id, output: 'segment', playback: 'video', metadata: 'fields',
      resultScope: 'all', time: hit.start_time, field: null,
    });
    setStudioSearchVideoError(null);
    const knownVideo = playgroundVideosRef.current.find((video) => video.video_id === hit.video_id);
    if (knownVideo) {
      setStudioSearchVideo(knownVideo);
    } else {
      setStudioSearchVideo(null);
      void apiService.getVideo(hit.video_id).then((video) => {
        if (generation === studioSearchSelectionGeneration.current) setStudioSearchVideo(video);
      }).catch((error) => {
        if (generation === studioSearchSelectionGeneration.current) setStudioSearchVideoError(getApiErrorMessage(error, 'Could not load this video.'));
      });
    }
  }, [setStudioSearchMode, setViewStateField]);
  useEffect(() => {
    if (studioSearchMode !== 'result' || studioSearchHit || !studioSearchState?.selectedResultKey) return;
    const hit = studioSearchResults.find((candidate) => getSearchResultKey(candidate) === studioSearchState.selectedResultKey);
    if (hit) openStudioSearchHit(hit);
  }, [studioSearchMode, studioSearchHit, studioSearchState?.selectedResultKey, studioSearchResults, openStudioSearchHit]);
  const updateAdmittedRun = useCallback((videoId: string, runId: string | null) => {
    if (runId) admittedRunByVideoRef.current.set(videoId, runId);
    else admittedRunByVideoRef.current.delete(videoId);
    setAdmittedRunByVideoId((current) => {
      if (runId && current[videoId] === runId) return current;
      if (!runId && !current[videoId]) return current;
      const next = { ...current };
      if (runId) next[videoId] = runId;
      else delete next[videoId];
      return next;
    });
  }, [setAdmittedRunByVideoId]);

  const [resultLink, setResultLink] = useState<ResultLinkTarget | null>(null);
  const [hydratedSearch, setHydratedSearch] = useState<string | null>(null);
  const urlHydrated = hydratedSearch !== null;
  const [playgroundVideos, setPlaygroundVideosState] = useState<Video[]>(() => (
    excludeAcceptedVideoDeletions(
      (playgroundTabState.videos || []) as Video[],
      acceptedVideoDeletionIdsRef.current,
    ).map(ensureDurableVideoPreviewFields)
  ));
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [hasLoadedInitialVideos, setHasLoadedInitialVideos] = useState(false);
  const [hasResolvedSelectedVideos, setHasResolvedSelectedVideos] = useState(false);
  const [selectionRestoreError, setSelectionRestoreError] = useState<string | null>(null);
  const [isLoadingMoreVideos, setIsLoadingMoreVideos] = useState(false);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [urlNotice, setUrlNotice] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<ErrorRecoveryAction | null>(null);
  const [requestedVideoId, setRequestedVideoId] = useState<string | null>(activeStudioVideoId);
  const [requestedRunPromptId, setRequestedRunPromptId] = useState<string | null>(null);
  const [studioHeaderActionsTarget, setStudioHeaderActionsTarget] = useState<HTMLDivElement | null>(null);
  const requestedStudioShouldOpenRef = useRef(false);
  const isPageVisible = usePageVisibility();
  const isWorkspaceVisible = isActive && isPageVisible;

  if (scrollPersistTimerRef.current === null) {
    latestScrollTopRef.current = containerRef.current?.scrollTop ?? scrollTop;
  }
  if (studioSelectorScrollPersistTimerRef.current === null) {
    latestStudioSelectorScrollTopRef.current = studioSelectorRef.current?.scrollTop
      ?? studioSelectorScrollTop;
  }
  const studioPlaybackScopeKey = `${activeStudioVideoId || ''}:${studioRunId || ''}`;
  if (playbackRestoreRef.current.scopeKey !== studioPlaybackScopeKey) {
    playbackRestoreRef.current = {
      scopeKey: studioPlaybackScopeKey,
      time: studioPlaybackTime,
    };
    latestPlaybackTimeRef.current = studioPlaybackTime;
    playbackTimeDirtyRef.current = false;
  }
  const studioRestorePlaybackTime = playbackRestoreRef.current.time;
  activeStudioVideoIdRef.current = activeStudioVideoId;
  studioRunIdRef.current = studioRunId;

  useEffect(() => {
    admittedRunByVideoRef.current = new Map(Object.entries(admittedRunByVideoId));
  }, [admittedRunByVideoId]);

  useEffect(() => {
    if (!pendingRunAttempt) {
      runAttemptRef.current = null;
      return;
    }
    if (
      runAttemptRef.current?.signature !== pendingRunAttempt.signature
      || runAttemptRef.current?.key !== pendingRunAttempt.key
    ) {
      runAttemptRef.current = { ...pendingRunAttempt };
    }
  }, [pendingRunAttempt]);

  playgroundVideosRef.current = playgroundVideos;

  const selectedVideoIds = useMemo(
    () => playgroundTabState.selectedVideos instanceof Set
      ? playgroundTabState.selectedVideos
      : new Set<string>(),
    [playgroundTabState.selectedVideos],
  );
  const selectedVideoIdsRef = useRef(selectedVideoIds);
  selectedVideoIdsRef.current = selectedVideoIds;
  const pendingVideoDeletions = useMemo(
    () => playgroundTabState.pendingVideoDeletions || {},
    [playgroundTabState.pendingVideoDeletions],
  );

  const updatePlaygroundState = useCallback((updates: Partial<typeof playgroundTabState>) => {
    setPlaygroundTabState((previous) => {
      const safeUpdates = { ...updates };
      if (Array.isArray(safeUpdates.videos)) {
        safeUpdates.videos = excludeAcceptedVideoDeletions(
          safeUpdates.videos,
          acceptedVideoDeletionIdsRef.current,
        );
      }
      return { ...previous, ...safeUpdates };
    });
  }, [setPlaygroundTabState]);

  const setSelectedVideoIds = useCallback((nextValue: React.SetStateAction<Set<string>>) => {
    setPlaygroundTabState((previous) => {
      const current = previous.selectedVideos instanceof Set
        ? previous.selectedVideos
        : new Set<string>();
      const next = typeof nextValue === 'function' ? nextValue(current) : nextValue;
      return { ...previous, selectedVideos: new Set(next) };
    });
  }, [setPlaygroundTabState]);

  const setPlaygroundVideos = useCallback((nextValue: React.SetStateAction<Video[]>) => {
    const current = playgroundVideosRef.current;
    const next = typeof nextValue === 'function' ? nextValue(current) : nextValue;
    // Match Index's gallery boundary: application state keeps durable preview
    // identities only. VideoTile acquires short-lived poster/animation grants.
    const sanitized = excludeAcceptedVideoDeletions(
      next,
      acceptedVideoDeletionIdsRef.current,
    ).map(ensureDurableVideoPreviewFields);
    const unchanged = sanitized.length === current.length
      && sanitized.every((video, index) => video === current[index]);
    if (unchanged) return;
    playgroundVideosRef.current = sanitized;
    setPlaygroundVideosState(sanitized);
    setPlaygroundTabState((previous) => (
      previous.videos === sanitized
        ? previous
        : { ...previous, videos: sanitized }
    ));
  }, [setPlaygroundTabState]);

  const updatePendingVideoDeletions = useCallback((
    updater: (current: PendingVideoDeletionMap) => PendingVideoDeletionMap,
  ) => {
    setPlaygroundTabState((previous) => {
      const current = previous.pendingVideoDeletions || {};
      const next = updater(current);
      return next === current ? previous : { ...previous, pendingVideoDeletions: next };
    });
  }, [setPlaygroundTabState]);

  useVideoDeletionPolling(pendingVideoDeletions, updatePendingVideoDeletions);

  const setApiError = useCallback((error: any, fallbackMessage: string) => {
    const insufficientCredits = extractInsufficientCredits(error);
    if (insufficientCredits) {
      const message = describeInsufficientCredits(insufficientCredits);
      updatePlaygroundState({ error: message });
      setErrorAction(createErrorRecoveryAction(message, {
        label: 'Add credits',
        path: insufficientCredits.purchasePath,
      }));
      return;
    }
    const planUpgrade = extractPlanUpgradeRequired(error);
    if (planUpgrade) {
      const message = planUpgrade.message || 'This action requires a paid plan.';
      updatePlaygroundState({ error: message });
      setErrorAction(createErrorRecoveryAction(message, {
        label: 'View plans',
        path: planUpgrade.details.upgrade_path || '/billing?section=plans',
      }));
      return;
    }
    updatePlaygroundState({ error: getApiErrorMessage(error, fallbackMessage) });
    setErrorAction(null);
  }, [updatePlaygroundState]);

  useEffect(() => {
    if (errorAction && errorAction.errorMessage !== playgroundTabState.error) {
      setErrorAction(null);
    }
  }, [errorAction, playgroundTabState.error]);

  const selectedMedia = useMemo(() => {
    const byId = new Map(playgroundVideos.map((video) => [video.video_id, video]));
    return Array.from(selectedVideoIds)
      .map((videoId) => byId.get(videoId))
      .filter((video): video is Video => Boolean(video));
  }, [playgroundVideos, selectedVideoIds]);

  const selectedPrompt = useMemo(() => {
    const promptId = playgroundTabState.selectedPrompt;
    if (!promptId) return null;
    return prompts.find((prompt) => prompt.prompt_id === promptId) || null;
  }, [playgroundTabState.selectedPrompt, prompts]);
  const selectedEngine = selectedPrompt;
  const activeStudioVideo = useMemo(() => {
    if (!activeStudioVideoId) return null;
    return playgroundVideos.find((video) => video.video_id === activeStudioVideoId) || null;
  }, [activeStudioVideoId, playgroundVideos]);
  const studioDetailIsOpen = Boolean(
    urlHydrated
    && activeStudioVideo !== null
    && requestedVideoId === activeStudioVideoId
  );
  const studioDetailIsVisible = isActive && studioDetailIsOpen && activeStep === 'studio' && studioSearchMode === 'studio';
  const studioWorkspaceScopeRunId = activeStudioVideoId ? studioRunId : null;
  const studioWorkspaceScopeMatches = Boolean(
    studioWorkspaceState
    && studioWorkspaceState.scopeVideoId === activeStudioVideoId
    && studioWorkspaceState.scopeRunId === studioWorkspaceScopeRunId,
  );
  const effectiveStudioWorkspaceState = useMemo(() => (
    studioWorkspaceScopeMatches && studioWorkspaceState
      ? studioWorkspaceState
      : createPlaygroundStudioWorkspaceState(
          activeStudioVideoId,
          studioWorkspaceScopeRunId,
        )
  ), [activeStudioVideoId, studioWorkspaceScopeMatches, studioWorkspaceScopeRunId, studioWorkspaceState]);
  const updateStudioWorkspaceState = useCallback((
    patch: Partial<PlaygroundStudioWorkspaceState>,
  ) => {
    if (isActive && activeStep === 'studio' && studioSearchMode === 'studio'
      && (patch.resultSearchTerm !== undefined || patch.resultFilter !== undefined)) {
      pendingStudioScrollTopRef.current = containerRef.current?.scrollTop ?? null;
    }
    setStudioWorkspaceState((current) => {
      const currentScopeMatches = Boolean(
        current
        && current.scopeVideoId === activeStudioVideoId
        && current.scopeRunId === studioWorkspaceScopeRunId,
      );
      const base = currentScopeMatches
        ? current
        : createPlaygroundStudioWorkspaceState(
            activeStudioVideoId,
            studioWorkspaceScopeRunId,
          );
      return {
        ...base,
        ...patch,
        scopeVideoId: activeStudioVideoId,
        scopeRunId: studioWorkspaceScopeRunId,
      };
    });
  }, [activeStudioVideoId, setStudioWorkspaceState, studioWorkspaceScopeRunId, isActive, activeStep, studioSearchMode]);

  useLayoutEffect(() => {
    pendingStudioScrollTopRef.current = null;
    if (studioResultSpaceRef.current) studioResultSpaceRef.current.style.height = '0px';
  }, [activeStudioVideoId, studioWorkspaceScopeRunId, activeStep, studioSearchMode, isActive]);

  useLayoutEffect(() => {
    const scrollTop = pendingStudioScrollTopRef.current;
    pendingStudioScrollTopRef.current = null;
    const container = containerRef.current;
    const spacer = studioResultSpaceRef.current;
    if (scrollTop !== null && container && spacer) {
      preserveScrollPosition(container, spacer, scrollTop);
    }
  }, [effectiveStudioWorkspaceState]);

  useEffect(() => {
    if (studioWorkspaceScopeMatches) return;
    setStudioWorkspaceState(createPlaygroundStudioWorkspaceState(
      activeStudioVideoId,
      studioWorkspaceScopeRunId,
    ));
  }, [activeStudioVideoId, setStudioWorkspaceState, studioWorkspaceScopeMatches, studioWorkspaceScopeRunId]);

  const mediaComplete = selectedMedia.length > 0;
  const engineComplete = Boolean(selectedPrompt);
  const canStartRun = mediaComplete && engineComplete;

  useEffect(() => {
    if (!playgroundTabStateReady || isLoadingPrompts || engineCatalogError) return;
    const selectedPromptId = playgroundTabState.selectedPrompt;
    if (!selectedPromptId) return;
    if (prompts.some((prompt) => prompt.prompt_id === selectedPromptId)) return;
    updatePlaygroundState({ selectedPrompt: '' });
    setRequestedRunPromptId(null);
  }, [engineCatalogError, isLoadingPrompts, playgroundTabState.selectedPrompt, playgroundTabStateReady, prompts, updatePlaygroundState]);

  useEffect(() => {
    // Defaults only apply after the current URL and stored workspace are read.
    // A linked video can still be resolving while the library already has rows.
    if (!playgroundTabStateReady || hydratedSearch !== location.search
      || requestedVideoId || activeStep !== 'studio' || activeStudioVideoId || playgroundVideos.length === 0) return;
    const initialStudioVideo = selectedMedia[0] || playgroundVideos[0];
    setRequestedVideoId(initialStudioVideo.video_id);
    setActiveStudioVideoId(initialStudioVideo.video_id);
    setStudioRunId(null);
    resetStudioPlaybackPosition();
  }, [activeStep, activeStudioVideoId, hydratedSearch, location.search, playgroundTabStateReady, playgroundVideos, requestedVideoId, resetStudioPlaybackPosition, selectedMedia, setActiveStudioVideoId, setStudioRunId]);

  const loadPlaygroundVideos = useCallback(async () => {
    const cachedVideos = playgroundVideosRef.current;
    setIsLoadingVideos(cachedVideos.length === 0);
    setHasResolvedSelectedVideos(false);
    setSelectionRestoreError(null);
    try {
      const response = await apiService.getPlaygroundVideos(VIDEOS_PAGE_SIZE);
      let pagedFresh = response.data.map((video) => applyRealtimeStateToVideo(
        video,
        processingRealtimeStore.getVideoState(video.video_id),
      ));
      let nextCursor = response.pagination.next_cursor || null;
      let hasMore = Boolean(response.pagination.has_more);
      const restoreTarget = paginationRestoreRef.current;
      const canReuseCachedDepth = Boolean(
        restoreTarget.loadedCount > pagedFresh.length
        && cachedVideos.length >= restoreTarget.loadedCount
        && (!restoreTarget.hasMore || restoreTarget.nextCursor),
      );

      if (canReuseCachedDepth) {
        nextCursor = restoreTarget.nextCursor;
        hasMore = restoreTarget.hasMore;
      } else {
        while (
          hasMore
          && nextCursor
          && pagedFresh.length < restoreTarget.loadedCount
        ) {
          const nextPage = await apiService.getPlaygroundVideos(VIDEOS_PAGE_SIZE, nextCursor);
          const projectedPage = nextPage.data.map((video) => applyRealtimeStateToVideo(
            video,
            processingRealtimeStore.getVideoState(video.video_id),
          ));
          pagedFresh = mergeVideos(pagedFresh, projectedPage);
          nextCursor = nextPage.pagination.next_cursor || null;
          hasMore = Boolean(nextPage.pagination.has_more);
        }
      }

      const loadedPageCount = canReuseCachedDepth
        ? restoreTarget.loadedCount
        : pagedFresh.length;
      let fresh = pagedFresh;
      const freshIds = new Set(fresh.map((video) => video.video_id));
      const missingSelectedIds = Array.from(selectedVideoIdsRef.current).filter(
        (videoId) => !freshIds.has(videoId),
      );
      if (missingSelectedIds.length > 0) {
        try {
          const selectionChunks: string[][] = [];
          for (let index = 0; index < missingSelectedIds.length; index += 100) {
            selectionChunks.push(missingSelectedIds.slice(index, index + 100));
          }
          const restoredSelections = (
            await Promise.all(selectionChunks.map((videoIds) => apiService.getBatchVideoSummaries(videoIds)))
          ).flat();
          fresh = mergeVideos(fresh, restoredSelections.map((video) => applyRealtimeStateToVideo(
            video,
            processingRealtimeStore.getVideoState(video.video_id),
          )));
          const restoredIds = new Set(restoredSelections.map((video) => video.video_id));
          setSelectedVideoIds((current) => new Set(
            Array.from(current).filter(
              (videoId) => !missingSelectedIds.includes(videoId) || restoredIds.has(videoId),
            ),
          ));
          setHasResolvedSelectedVideos(true);
        } catch (selectionError) {
          console.warn('Could not restore all selected Playground media:', selectionError);
          setSelectionRestoreError('Some selected media could not be restored.');
        }
      } else {
        setHasResolvedSelectedVideos(true);
      }
      const currentWithRealtime = playgroundVideosRef.current.map((video) => (
        applyRealtimeStateToVideo(video, processingRealtimeStore.getVideoState(video.video_id))
      ));
      setPlaygroundVideos(mergeVideos(fresh, currentWithRealtime));
      paginationRestoreRef.current = {
        nextCursor,
        hasMore,
        loadedCount: loadedPageCount,
      };
      setVideosNextCursor(nextCursor);
      setHasMoreVideos(hasMore);
      setLoadedVideoCount(loadedPageCount);
    } catch (error) {
      console.error('Failed to load playground media:', error);
      setApiError(error, 'Could not load your media library. Please try again.');
    } finally {
      setIsLoadingVideos(false);
      setHasLoadedInitialVideos(true);
    }
  }, [setApiError, setHasMoreVideos, setLoadedVideoCount, setPlaygroundVideos, setSelectedVideoIds, setVideosNextCursor]);

  useEffect(() => {
    if (playgroundTabStateReady) loadPlaygroundVideos();
  }, [loadPlaygroundVideos, playgroundTabStateReady]);

  const loadMoreVideos = useCallback(async () => {
    if (!videosNextCursor || isLoadingMoreVideos) return;
    setIsLoadingMoreVideos(true);
    try {
      const response = await apiService.getPlaygroundVideos(VIDEOS_PAGE_SIZE, videosNextCursor);
      const fresh = response.data.map((video) => applyRealtimeStateToVideo(
        video,
        processingRealtimeStore.getVideoState(video.video_id),
      ));
      const existingIds = new Set(playgroundVideosRef.current.map((video) => video.video_id));
      const addedPageCount = fresh.reduce(
        (count, video) => count + (existingIds.has(video.video_id) ? 0 : 1),
        0,
      );
      setPlaygroundVideos((current) => mergeVideos(current, fresh));
      const nextCursor = response.pagination.next_cursor || null;
      const hasMore = Boolean(response.pagination.has_more);
      const nextLoadedCount = paginationRestoreRef.current.loadedCount + addedPageCount;
      paginationRestoreRef.current = {
        nextCursor,
        hasMore,
        loadedCount: nextLoadedCount,
      };
      setVideosNextCursor(nextCursor);
      setHasMoreVideos(hasMore);
      setLoadedVideoCount(nextLoadedCount);
    } catch (error) {
      console.error('Failed to load more playground media:', error);
      setApiError(error, 'Could not load more media. Please try again.');
    } finally {
      setIsLoadingMoreVideos(false);
    }
  }, [isLoadingMoreVideos, setApiError, setHasMoreVideos, setLoadedVideoCount, setPlaygroundVideos, setVideosNextCursor, videosNextCursor]);

  const visibleVideoIds = useMemo(
    () => playgroundVideos.map((video) => video.video_id),
    [playgroundVideos],
  );

  useEffect(() => {
    if (!hasLoadedInitialVideos || !hasResolvedSelectedVideos || isLoadingVideos || selectedVideoIds.size === 0) return;
    const visibleIds = new Set(visibleVideoIds);
    const hasHiddenSelection = Array.from(selectedVideoIds).some(
      (videoId) => !visibleIds.has(videoId),
    );
    if (!hasHiddenSelection) return;
    setSelectedVideoIds(new Set(
      Array.from(selectedVideoIds).filter((videoId) => visibleIds.has(videoId)),
    ));
  }, [hasLoadedInitialVideos, hasResolvedSelectedVideos, isLoadingVideos, selectedVideoIds, setSelectedVideoIds, visibleVideoIds]);

  const shouldSubscribeToProcessingStream = useMemo(() => (
    isWorkspaceVisible
  ), [isWorkspaceVisible]);
  const processingVideoIdsKey = useMemo(() => (
    playgroundVideos
      .filter((video) => isVideoInProgress(video))
      .map((video) => video.video_id)
      .sort()
      .join(',')
  ), [playgroundVideos]);

  useEffect(() => {
    const snapshot = playgroundVideosRef.current;
    if (!isWorkspaceVisible || !snapshot.some((video) => isVideoInProgress(video))) return;
    let cancelled = false;
    const requestId = ++statusReconcileRequestRef.current;
    const reconcile = async () => {
      try {
        const videoIds = snapshot.map((video) => video.video_id).filter(Boolean);
        const chunks: string[][] = [];
        for (let index = 0; index < videoIds.length; index += 100) chunks.push(videoIds.slice(index, index + 100));
        const statuses = (await Promise.all(chunks.map((chunk) => apiService.getBatchVideoStatus(chunk)))).flat();
        if (cancelled || requestId !== statusReconcileRequestRef.current) return;
        const statusByVideoId = new Map(statuses.map((entry) => [entry.video_id, entry]));
        const admittedRunIdsNowVisible: string[] = [];
        setPlaygroundVideos((current) => current.map((video) => {
          const latest = statusByVideoId.get(video.video_id);
          if (!latest) return video;
          const admittedRunId = admittedRunByVideoRef.current.get(video.video_id);
          if (admittedRunId) {
            const admittedRunIsVisible = (latest.processing_status || []).some(
              (run) => run.run_id === admittedRunId,
            );
            if (!admittedRunIsVisible) return video;
            admittedRunIdsNowVisible.push(video.video_id);
          }
          const localIsTerminal = video.status === 'processed' || video.status === 'failed';
          if (localIsTerminal && isInProgressStatus(latest.status)) return video;
          const nextStatus = latest.status || video.status;
          const nextProcessingStatus = latest.processing_status || [];
          if (nextStatus === video.status && JSON.stringify(nextProcessingStatus) === JSON.stringify(video.processing_status || [])) return video;
          return { ...video, status: nextStatus, processing_status: nextProcessingStatus };
        }));
        admittedRunIdsNowVisible.forEach((videoId) => updateAdmittedRun(videoId, null));
      } catch (error) {
        console.warn('Failed to reconcile playground media statuses:', error);
      }
    };
    reconcile();
    return () => { cancelled = true; };
  }, [isWorkspaceVisible, processingVideoIdsKey, setPlaygroundVideos, updateAdmittedRun]);

  useEffect(() => {
    if (!shouldSubscribeToProcessingStream) return;
    return processingRealtimeStore.subscribe((event) => {
      if (event.event_type === 'video.discovered' || event.event_type === 'import_job.video_imported') {
        const video = event.payload?.video;
        if (video && (!event.index_id || event.index_id === authScopeId)) {
          setPlaygroundVideos((current) => mergeVideos([video], current));
          if (event.event_type === 'import_job.video_imported') {
            setSelectedVideoIds((current) => new Set([...Array.from(current), video.video_id]));
          }
        }
      }
      if (!event.video_id) return;
      if (!playgroundVideosRef.current.some((video) => video.video_id === event.video_id)) {
        return;
      }
      const admittedRunId = admittedRunByVideoRef.current.get(event.video_id);
      const projection = processingRealtimeStore.getVideoState(event.video_id)?.processing_status?.find(
        (run) => run.run_id === admittedRunId,
      );
      if (admittedRunId && projection) updateAdmittedRun(event.video_id, null);
      setPlaygroundVideos((current) => {
        const videoIndex = current.findIndex((video) => video.video_id === event.video_id);
        if (videoIndex < 0) return current;
        const currentVideo = current[videoIndex];
        const updatedVideo = applyRealtimeStateToVideo(
          applyVideoAssetsReadyEvent(currentVideo, event),
          processingRealtimeStore.getVideoState(currentVideo.video_id),
        );
        if (updatedVideo === currentVideo) return current;
        const updatedVideos = [...current];
        updatedVideos[videoIndex] = updatedVideo;
        return updatedVideos;
      });
    }, { video_ids: playgroundVideosRef.current.map((video) => video.video_id).slice(0, 100) });
  }, [activeStudioVideoId, authScopeId, processingVideoIdsKey, setPlaygroundVideos, setSelectedVideoIds, shouldSubscribeToProcessingStream, studioDetailIsOpen, updateAdmittedRun]);

  useEffect(() => {
    if (!playgroundTabStateReady) return;
    const params = new URLSearchParams(location.search);
    const requestedStep = params.get('step');
    const validRequestedStep: JourneyStep | null = requestedStep === 'review'
      ? 'engine'
      : requestedStep === 'media' || requestedStep === 'engine' || requestedStep === 'studio'
        ? requestedStep
        : null;
    const target = parseResultLink('/playground', location.search);
    setResultLink(target);
    const urlVideoId = params.get('video');
    const urlRunId = params.get('run');
    if (validRequestedStep || requestedStep === 'collapsed') setActiveStep(validRequestedStep);
    requestedStudioShouldOpenRef.current = Boolean(urlVideoId && !validRequestedStep && requestedStep !== 'collapsed');
    if (urlVideoId) {
      if (
        activeStudioVideoIdRef.current !== urlVideoId
        || studioRunIdRef.current !== urlRunId
      ) {
        resetStudioPlaybackPosition();
      }
      setRequestedVideoId(urlVideoId);
      setStudioRunId(urlRunId);
    } else {
      // A clean /playground navigation comes from the sidebar. Restore the
      // persisted Studio target instead of interpreting the missing query as a
      // request to discard the workspace.
      setRequestedVideoId(activeStudioVideoIdRef.current);
    }
    setRequestedRunPromptId(params.get('runPrompt'));
    historyModeRef.current = 'replace';

    // Publish the consumed URL in the same React update as its view state.
    // A timer/ref gate can suppress a quick toggle without scheduling another
    // render, leaving the old URL to restore a section the user just closed.
    setHydratedSearch(location.search);
  }, [location.search, playgroundTabStateReady, resetStudioPlaybackPosition, setActiveStep, setStudioRunId]);

  useEffect(() => {
    if (
      !requestedRunPromptId
      || isLoadingPrompts
      || (engineCatalogError && prompts.length === 0)
    ) return;
    const requestedPrompt = prompts.find((prompt) => prompt.prompt_id === requestedRunPromptId);
    if (requestedPrompt) {
      updatePlaygroundState({ selectedPrompt: requestedPrompt.prompt_id });
      return;
    }
    setUrlNotice('The requested extraction engine is not available.');
    setRequestedRunPromptId(null);
    updatePlaygroundState({ selectedPrompt: '' });
  }, [engineCatalogError, isLoadingPrompts, prompts, requestedRunPromptId, updatePlaygroundState]);

  useEffect(() => {
    if (!playgroundTabStateReady || hydratedSearch !== location.search) return;
    if (!requestedVideoId) {
      lastAppliedStudioRequestRef.current = null;
      return;
    }
    if (acceptedVideoDeletionIdsRef.current.has(requestedVideoId)) {
      setUrlNotice('The requested media item is being deleted and is no longer available.');
      setRequestedVideoId(null);
      return;
    }
    const localVideo = playgroundVideosRef.current.find(
      (video) => video.video_id === requestedVideoId,
    );
    if (localVideo) {
      setActiveStudioVideoId(localVideo.video_id);
      const requestedStudioKey = `${requestedVideoId}:${studioRunId || ''}`;
      if (
        requestedStudioShouldOpenRef.current
        && lastAppliedStudioRequestRef.current !== requestedStudioKey
      ) {
        lastAppliedStudioRequestRef.current = requestedStudioKey;
        setActiveStep('studio');
      }
      return;
    }
    let cancelled = false;
    apiService.getVideo(requestedVideoId)
      .then((video) => {
        if (cancelled || acceptedVideoDeletionIdsRef.current.has(video.video_id)) return;
        setActiveStudioVideoId(video.video_id);
        setPlaygroundVideos((current) => mergeVideos(current, [video]));
        lastAppliedStudioRequestRef.current = `${video.video_id}:${studioRunId || ''}`;
        if (requestedStudioShouldOpenRef.current) setActiveStep('studio');
      })
      .catch(() => {
        if (cancelled) return;
        setUrlNotice('The requested media item is no longer available.');
        setRequestedVideoId(null);
        if (activeStudioVideoIdRef.current === requestedVideoId) setActiveStudioVideoId(null);
        setStudioRunId(null);
      });
    return () => { cancelled = true; };
  }, [hydratedSearch, location.search, playgroundTabStateReady, requestedVideoId, setActiveStep, setActiveStudioVideoId, setPlaygroundVideos, setStudioRunId, studioRunId]);

  useEffect(() => {
    if (
      !isActive
      || !playgroundTabStateReady
      || hydratedSearch !== location.search
      || requestedVideoId !== activeStudioVideoId
      || (
        requestedRunPromptId !== null
        && requestedRunPromptId !== selectedPrompt?.prompt_id
      )
    ) return;
    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        view: null,
        step: activeStep || 'collapsed',
        video: activeStudioVideoId,
        run: activeStudioVideoId ? studioRunId : null,
        runPrompt: selectedPrompt?.prompt_id || null,
        ...resultViewParams(resultLink?.videoId === activeStudioVideoId && resultLink?.runId === studioRunId ? resultLink : null),
        vq: null,
        vsort: null,
        vdir: null,
      },
      mode,
    });
  }, [activeStep, activeStudioVideoId, hydratedSearch, isActive, location, navigate, playgroundTabStateReady, requestedRunPromptId, requestedVideoId, selectedPrompt?.prompt_id, studioRunId, resultLink]);

  const handleUploadFile = useCallback(async (file: File) => {
    if (!isSupportedLocalMediaFile(file)) {
      updatePlaygroundState({ error: 'Choose a supported video, audio, or image file.' });
      return;
    }
    if (exceedsMediaUploadSizeLimit(file.size)) {
      updatePlaygroundState({ error: MEDIA_UPLOAD_SIZE_LIMIT_MESSAGE });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (!authScopeId) {
      updatePlaygroundState({ error: 'Your authenticated session is not available. Sign in and try again.' });
      return;
    }
    updatePlaygroundState({ error: null });
    try {
      uploadQueue.open('playground', 'Playground');
      uploadQueue.addFiles([file]);
      uploadQueue.start();
    } catch (error) {
      setApiError(error, 'Upload could not be started.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [authScopeId, setApiError, updatePlaygroundState]);

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleUploadFile(file);
  };

  const choosePrompt = useCallback((prompt: Prompt) => {
    updatePlaygroundState({ selectedPrompt: prompt.prompt_id, error: null });
    setRequestedRunPromptId(prompt.prompt_id);
  }, [updatePlaygroundState]);

  const handleStartRun = useCallback(async () => {
    const videoIds = selectedMedia.map((video) => video.video_id);
    if (videoIds.length === 0) {
      setActiveStep('media');
      updatePlaygroundState({ error: 'Choose at least one media item to continue.' });
      return;
    }
    if (!selectedEngine) {
      setActiveStep('engine');
      updatePlaygroundState({ error: 'Choose what you want to extract first.' });
      return;
    }
    setIsStartingRun(true);
    updatePlaygroundState({ error: null });
    try {
      const promptForRun = selectedPrompt;
      if (!promptForRun) throw new Error('Selected extraction engine is unavailable.');
      await Promise.all(videoIds.map((videoId) => segmentCache.clearVideoCache(videoId)));
      const request = {
        prompt_id: promptForRun.prompt_id,
        target: { type: 'videos' as const, video_ids: videoIds },
      };
      const runSignature = JSON.stringify(request);
      let runAttempt = runAttemptRef.current;
      if (!runAttempt || runAttempt.signature !== runSignature) {
        runAttempt = { signature: runSignature, key: generateIdempotencyKey('playground-run') };
        runAttemptRef.current = runAttempt;
        setPendingRunAttempt({ signature: runAttempt.signature, key: runAttempt.key });
      }
      let promptRun: PromptRun;
      try {
        promptRun = await apiService.executePromptRun(request, runAttempt.key);
      } catch (error) {
        if (!isRetryableStreamRequestError(error)) {
          runAttemptRef.current = null;
          setPendingRunAttempt(null);
        }
        throw error;
      }
      runAttemptRef.current = null;
      setPendingRunAttempt(null);
      videoIds.forEach((videoId) => {
        updateAdmittedRun(videoId, promptRun.run_id);
      });
      setPlaygroundVideos((current) => current.map((video) => {
        if (!videoIds.includes(video.video_id)) return video;
        const existingRun = (video.processing_status || []).find(
          (run) => run.run_id === promptRun.run_id,
        );
        const realtimeState = processingRealtimeStore.getVideoState(video.video_id);
        const realtimeOwnsRun = Boolean(
          realtimeState?.processing_status?.some((run) => run.run_id === promptRun.run_id),
        );

        // The processing stream can publish progress (or even a terminal
        // result) before the execute response reaches the browser. Preserve
        // that newer projection instead of replacing it with HTTP's initial
        // pending snapshot.
        if (realtimeOwnsRun) {
          return applyRealtimeStateToVideo(video, realtimeState);
        }
        if (existingRun) {
          return video;
        }

        const processingStatus = [
          ...(video.processing_status || []),
          {
            run_id: promptRun.run_id,
            prompt_id: promptForRun.prompt_id,
            status: promptRun.status || 'pending',
            total_segments: promptRun.total_segments || 0,
            pending_segments: promptRun.total_segments || 0,
            processing_segments: 0,
            successful_segments: 0,
            failed_segments: 0,
            segments: [],
          },
        ];
        return { ...video, status: 'processing', processing_status: processingStatus };
      }));
      const firstVideo = selectedMedia[0];
      historyModeRef.current = 'push';
      lastAppliedStudioRequestRef.current = `${firstVideo.video_id}:${promptRun.run_id}`;
      setRequestedVideoId(firstVideo.video_id);
      setActiveStudioVideoId(firstVideo.video_id);
      setStudioRunId(promptRun.run_id);
      resetStudioPlaybackPosition();
      setStudioSearchMode('studio');
      setActiveStep('studio');
    } catch (error) {
      console.error('Failed to start playground extraction:', error);
      setApiError(error, 'Could not start extraction. Please try again.');
    } finally {
      setIsStartingRun(false);
    }
  }, [resetStudioPlaybackPosition, selectedEngine, selectedMedia, selectedPrompt, setActiveStep, setActiveStudioVideoId, setApiError, setPendingRunAttempt, setPlaygroundVideos, setStudioRunId, setStudioSearchMode, updateAdmittedRun, updatePlaygroundState]);

  const handleVideoTileClick = useCallback((video: Video) => {
    setStudioSearchMode('studio');
    const isCurrentStudioVideo = video.video_id === activeStudioVideoId;
    setResultLink(null);
    historyModeRef.current = 'push';
    lastAppliedStudioRequestRef.current = `${video.video_id}:${isCurrentStudioVideo ? studioRunId || '' : ''}`;
    setRequestedVideoId(video.video_id);
    setActiveStudioVideoId(video.video_id);
    if (!isCurrentStudioVideo) {
      setStudioRunId(null);
      resetStudioPlaybackPosition();
    }
    setActiveStep('studio');
  }, [activeStudioVideoId, resetStudioPlaybackPosition, setActiveStep, setActiveStudioVideoId, setStudioRunId, studioRunId, setStudioSearchMode]);

  const handleDeleteVideo = useCallback(async (videoId: string) => {
    if (!window.confirm('Permanently delete this media item?')) return;
    try {
      const target = playgroundVideos.find((video) => video.video_id === videoId);
      const deletion = await apiService.deleteVideo(videoId);
      acceptedVideoDeletionIdsRef.current.add(videoId);
      updateAdmittedRun(videoId, null);
      setPlaygroundTabState((previous) => {
        const selected = new Set(previous.selectedVideos || []);
        selected.delete(videoId);
        return {
          ...previous,
          selectedVideos: selected,
          pendingVideoDeletions: recordAcceptedVideoDeletion(
            previous.pendingVideoDeletions || {},
            deletion,
            target?.title || videoId,
            new Date().toISOString(),
          ),
        };
      });
      setPlaygroundVideos((current) => current.filter((video) => video.video_id !== videoId));
      await segmentCache.clearVideoCache(videoId);
      if (activeStudioVideoId === videoId) {
        historyModeRef.current = 'push';
        setRequestedVideoId(null);
        setActiveStudioVideoId(null);
        setStudioRunId(null);
        resetStudioPlaybackPosition();
        setActiveStep(selectedMedia.length > 1 ? 'engine' : 'media');
      }
    } catch (error) {
      console.error('Failed to delete playground media:', error);
      setApiError(error, 'Could not delete this media item.');
    }
  }, [activeStudioVideoId, playgroundVideos, resetStudioPlaybackPosition, selectedMedia.length, setActiveStep, setActiveStudioVideoId, setApiError, setPlaygroundTabState, setPlaygroundVideos, setStudioRunId, updateAdmittedRun]);

  const visibleErrorAction = getVisibleErrorRecoveryAction(playgroundTabState.error, errorAction);
  const handleJourneyStepActivate = useCallback((step: JourneyStep) => {
    if (activeStep === step) {
      setActiveStep(null);
      return;
    }
    if (step === 'studio' && !activeStudioVideoId && playgroundVideos.length > 0) {
      const initialStudioVideo = selectedMedia[0] || playgroundVideos[0];
      setRequestedVideoId(initialStudioVideo.video_id);
      setActiveStudioVideoId(initialStudioVideo.video_id);
      setStudioRunId(null);
      resetStudioPlaybackPosition();
    }
    setActiveStep(step);
  }, [activeStep, activeStudioVideoId, playgroundVideos, resetStudioPlaybackPosition, selectedMedia, setActiveStep, setActiveStudioVideoId, setStudioRunId]);

  const handleContainerScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const spacer = studioResultSpaceRef.current;
    if (spacer?.offsetHeight) preserveScrollPosition(event.currentTarget, spacer, event.currentTarget.scrollTop);
    latestScrollTopRef.current = event.currentTarget.scrollTop;
    if (scrollPersistTimerRef.current !== null) return;
    scrollPersistTimerRef.current = window.setTimeout(() => {
      scrollPersistTimerRef.current = null;
      setViewStateField('scrollTop', latestScrollTopRef.current);
    }, 160);
  }, [setViewStateField]);

  const handleStudioSelectorScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    latestStudioSelectorScrollTopRef.current = event.currentTarget.scrollTop;
    if (studioSelectorScrollPersistTimerRef.current !== null) return;
    studioSelectorScrollPersistTimerRef.current = window.setTimeout(() => {
      studioSelectorScrollPersistTimerRef.current = null;
      setStudioSelectorScrollTop(latestStudioSelectorScrollTopRef.current);
    }, 160);
  }, [setStudioSelectorScrollTop]);

  const handleStudioPlaybackTimeChange = useCallback((time: number) => {
    if (!Number.isFinite(time) || time < 0) return;
    latestPlaybackTimeRef.current = time;
    playbackTimeDirtyRef.current = true;
  }, []);

  const flushStudioPlaybackTime = useCallback(() => {
    if (!playbackTimeDirtyRef.current) return;
    playbackTimeDirtyRef.current = false;
    commitPlaygroundViewState({
      studioPlaybackTime: latestPlaybackTimeRef.current,
    });
  }, [commitPlaygroundViewState]);

  useEffect(() => {
    const handlePageHide = () => flushStudioPlaybackTime();
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [flushStudioPlaybackTime]);

  useEffect(() => {
    if (!isWorkspaceVisible) flushStudioPlaybackTime();
  }, [flushStudioPlaybackTime, isWorkspaceVisible]);

  useEffect(() => {
    if (activeStep === 'studio' || !playbackTimeDirtyRef.current) return;
    playbackRestoreRef.current = {
      scopeKey: studioPlaybackScopeKey,
      time: latestPlaybackTimeRef.current,
    };
    flushStudioPlaybackTime();
  }, [activeStep, flushStudioPlaybackTime, studioPlaybackScopeKey]);

  const handleStudioRunSelectionChange = useCallback((
    runId: string | null,
    options?: { userInitiated?: boolean },
  ) => {
    if (!activeStudioVideoId) return;
    historyModeRef.current = options?.userInitiated ? 'push' : 'replace';
    lastAppliedStudioRequestRef.current = `${activeStudioVideoId}:${runId || ''}`;
    if (runId !== studioRunId) {
      resetStudioPlaybackPosition();
      setResultLink((current) => options?.userInitiated ? null : current ? { ...current, runId } : null);
    }
    setStudioRunId(runId);
  }, [activeStudioVideoId, resetStudioPlaybackPosition, setStudioRunId, studioRunId]);

  const handleCloseStudio = useCallback(() => {
    setActiveStep(mediaComplete ? 'engine' : 'media');
  }, [mediaComplete, setActiveStep]);

  useEffect(() => {
    if (
      !playgroundTabStateReady
      || !hasLoadedInitialVideos
      || hasRestoredScrollRef.current
    ) return;
    hasRestoredScrollRef.current = true;
    const frameId = window.requestAnimationFrame(() => {
      const container = containerRef.current;
      if (container) container.scrollTop = scrollTop;
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [hasLoadedInitialVideos, playgroundTabStateReady, scrollTop]);

  useEffect(() => {
    if (
      !playgroundTabStateReady
      || !hasLoadedInitialVideos
      || activeStep !== 'studio'
      || playgroundVideos.length === 0
      || hasRestoredStudioSelectorScrollRef.current
    ) return;
    hasRestoredStudioSelectorScrollRef.current = true;
    const frameId = window.requestAnimationFrame(() => {
      const selector = studioSelectorRef.current;
      if (selector) selector.scrollTop = studioSelectorScrollTop;
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [activeStep, hasLoadedInitialVideos, playgroundTabStateReady, playgroundVideos.length, studioSelectorScrollTop]);

  useEffect(() => () => {
    if (scrollPersistTimerRef.current !== null) {
      window.clearTimeout(scrollPersistTimerRef.current);
      scrollPersistTimerRef.current = null;
    }
    if (studioSelectorScrollPersistTimerRef.current !== null) {
      window.clearTimeout(studioSelectorScrollPersistTimerRef.current);
      studioSelectorScrollPersistTimerRef.current = null;
    }
    playbackTimeDirtyRef.current = false;
    commitPlaygroundViewState({
      scrollTop: latestScrollTopRef.current,
      studioSelectorScrollTop: latestStudioSelectorScrollTopRef.current,
      studioPlaybackTime: latestPlaybackTimeRef.current,
    });
  }, [commitPlaygroundViewState]);

  return (
    <PlaygroundFeatureAccessProvider>
    <div ref={containerRef} className="playground-container" onScroll={handleContainerScroll}>
      <header className="playground-hero">
        <div>
          <h1>Turn media into structured results</h1>
          <p>Find answers and extract the details you need from your media.</p>
        </div>
      </header>

      {(playgroundTabState.error || urlNotice) && (
        <div className="playground-alert playground-alert--error" role="alert">
          <span>{urlNotice || playgroundTabState.error}</span>
          {!urlNotice && playgroundTabState.error && visibleErrorAction && (
            <button type="button" onClick={() => navigate(visibleErrorAction.path)}>{visibleErrorAction.label}</button>
          )}
          <button
            type="button"
            className="playground-alert__close"
            aria-label="Dismiss message"
            onClick={() => {
              setUrlNotice(null);
              updatePlaygroundState({ error: null });
              setErrorAction(null);
            }}
          ><X size={16} /></button>
        </div>
      )}

      {Object.entries(pendingVideoDeletions).map(([videoId, deletion]) => (
        <div key={videoId} className="playground-alert" role="status" aria-live="polite">
          <RefreshCw size={15} className="playground-spinning" />
          <span>Deleting {deletion.video_title} — {deletion.status}</span>
        </div>
      ))}

      <div className="playground-journey">
        <JourneySection
          step="media"
          number={1}
          title="Choose media"
          summary={formatMediaSummary(selectedMedia)}
          activeStep={activeStep}
          complete={mediaComplete}
          onActivate={handleJourneyStepActivate}
          collapsedContent={selectedMedia.length > 0 ? (
            <div className="playground-collapsed-media" aria-label="Selected media">
              {selectedMedia.slice(0, 4).map((video) => (
                <div key={video.video_id} className="playground-collapsed-media__item">
                  <VideoTile
                    video={video}
                    onTileClick={handleVideoTileClick}
                    onDelete={handleDeleteVideo}
                    isProcessing={video.status === 'processing'}
                    isReadOnly
                    isInteractive={false}
                    showMarker={false}
                    showMetadataKeys={false}
                    showStatusText={false}
                    showTopStatusBar={false}
                  />
                </div>
              ))}
              {selectedMedia.length > 4 && (
                <span className="playground-collapsed-media__more">+{selectedMedia.length - 4}</span>
              )}
            </div>
          ) : undefined}
        >
          <input ref={fileInputRef} type="file" accept={LOCAL_MEDIA_FILE_ACCEPT} onChange={handleFileInput} hidden />
          <div
            className="playground-media-chooser"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files?.[0];
              if (file) handleUploadFile(file);
            }}
          >
            {selectionRestoreError && (
              <div className="playground-inline-error" role="status">
                <span>{selectionRestoreError}</span>
                <button type="button" onClick={loadPlaygroundVideos}>Try again</button>
              </div>
            )}

            <div id="playground-media-library" className="playground-media-library">
              <PreviewViewportProvider>
                <div
                  className={`playground-media-grid ${playgroundVideos.length === 0 ? 'is-empty' : ''}`}
                  aria-label="Choose media"
                >
                  <button
                    type="button"
                    className="playground-upload-tile"
                    onClick={() => uploadQueue.open('playground', 'Playground')}
                    aria-label="Upload media"
                  >
                    <Upload size={26} />
                    <span>{playgroundVideos.length === 0
                      ? 'Upload files or import URLs'
                      : 'Add media'}</span>
                    <small>Up to {MEDIA_UPLOAD_SIZE_LIMIT_LABEL} per file</small>
                  </button>
                  {playgroundVideos.map((video) => (
                    <DeferredPreviewTile key={video.video_id}>
                      <VideoTile
                        video={video}
                        onTileClick={handleVideoTileClick}
                        onDelete={handleDeleteVideo}
                        isProcessing={video.status === 'processing'}
                        isSelected={selectedVideoIds.has(video.video_id)}
                        onSelectionChange={(videoId, selected) => {
                          setSelectedVideoIds((current) => {
                            const next = new Set(current);
                            if (selected) next.add(videoId); else next.delete(videoId);
                            return next;
                          });
                        }}
                        isActiveInSidebar={activeStudioVideoId === video.video_id}
                        showMarker={false}
                        showMetadataKeys={false}
                        showStatusText={false}
                        showTopStatusBar={false}
                      />
                    </DeferredPreviewTile>
                  ))}
                </div>
              </PreviewViewportProvider>

              {(hasMoreVideos || selectedMedia.length > 0) && (
                <div className="playground-media-actions">
                  {hasMoreVideos && (
                    <button type="button" className="playground-button playground-button--secondary" onClick={loadMoreVideos} disabled={isLoadingMoreVideos}>
                      {isLoadingMoreVideos ? <RefreshCw size={16} className="playground-spinning" /> : <ChevronDown size={16} />}
                      {isLoadingMoreVideos ? 'Loading…' : 'Load more media'}
                    </button>
                  )}
                  {selectedMedia.length > 0 && (
                    <button type="button" className="playground-button playground-button--primary" onClick={() => setActiveStep('engine')}>
                      Continue with {selectedMedia.length} {selectedMedia.length === 1 ? 'item' : 'items'}<ChevronRight size={17} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </JourneySection>

        <JourneySection
          step="engine"
          number={2}
          title="Choose what to extract"
          summary={activeStep === 'engine'
            ? 'Select an extraction engine or create one'
            : selectedEngine?.name || 'Select an extraction engine'}
          activeStep={activeStep}
          complete={engineComplete}
          onActivate={handleJourneyStepActivate}
        >
          <EngineGallery
            selectedPromptId={playgroundTabState.selectedPrompt || ''}
            onSelect={choosePrompt}
            isActive={isActive && activeStep === 'engine'}
            disabled={isStartingRun}
            manageEditorLinks={false}
          />

          <div className="playground-start-action">
            <button
              type="button"
              className="playground-button playground-button--primary playground-button--large"
              onClick={handleStartRun}
              disabled={!canStartRun || isStartingRun}
            >
              {isStartingRun ? <RefreshCw size={18} className="playground-spinning" /> : <Play size={18} />}
              {isStartingRun ? 'Starting extraction…' : 'Start processing'}
            </button>
          </div>
        </JourneySection>

        <JourneySection
          step="studio"
          number={3}
          title="Studio"
          summary="Search your media or inspect its segments and extracted fields"
          activeStep={activeStep}
          complete={Boolean(activeStudioVideo)}
          headerActionsRef={activeStudioVideo ? setStudioHeaderActionsTarget : undefined}
          onActivate={handleJourneyStepActivate}
        >
          <div className="playground-studio">
            <StudioVideoRail
              className="playground-studio-selector"
              videos={playgroundVideos}
              selectedVideoId={studioSearchMode === 'result' ? studioSearchHit?.video_id : activeStudioVideoId}
              searchActive={studioSearchMode === 'results'}
              onSearch={() => {
                setHasOpenedStudioSearch(true);
                setStudioSearchMode('results');
              }}
              onSelectVideo={handleVideoTileClick}
              isLoading={isLoadingVideos}
              hasMore={hasMoreVideos}
              isLoadingMore={isLoadingMoreVideos}
              onLoadMore={loadMoreVideos}
              onChooseMedia={() => setActiveStep('media')}
              scrollRef={studioSelectorRef}
              onScroll={handleStudioSelectorScroll}
              isActive={isActive && activeStep === 'studio'}
            />

            {hasOpenedStudioSearch && <div className="playground-studio-search-region studio-search-surface" hidden={studioSearchMode !== 'results'}>
              <StudioSearchWorkspace
                transport={studioSearchTransport}
                state={studioSearchState}
                onStateChange={setStudioSearchState}
                onResultsChange={setStudioSearchResults}
                onSelectResult={openStudioSearchHit}
                onExit={() => setStudioSearchMode('studio')}
                isActive={isActive && activeStep === 'studio' && studioSearchMode === 'results'}
                refreshKey={JSON.stringify([playgroundVideos.map((video) => [video.video_id, video.status, video.thumbnail_gcs_uri, video.gif_gcs_uri, video.processing_status?.map((run) => [run.run_id, run.status])]), admittedRunByVideoId])}
              />
            </div>}
            {hasOpenedStudioSearch && <div className="playground-studio-search-region studio-search-surface" hidden={studioSearchMode !== 'result'}>
              {studioSearchHit && studioSearchVideo?.video_id === studioSearchHit.video_id ? <VideoDetailModal
                video={studioSearchVideo}
                isOpen
                isVisible={isActive && activeStep === 'studio' && studioSearchMode === 'result'}
                presentation="embedded"
                playbackMode="continuous-video"
                indexId="playground"
                deepLinkRunId={getSearchResultRunId(studioSearchHit)}
                resultLink={studioSearchResultLink}
                onResultLinkChange={(patch) => setStudioSearchResultLink((previous) => previous ? { ...previous, ...patch } : previous)}
                onClose={() => setStudioSearchMode('results')}
                searchContext={{ results: studioSearchResults, navigation: <>
                  <button type="button" className="studio-search-back-link" aria-label="Back to results" onClick={() => setStudioSearchMode('results')}><ArrowLeft size={14} aria-hidden="true" />Results</button>
                  <StudioSearchResultCarousel results={studioSearchResults} selectedResultKey={getSearchResultKey(studioSearchHit)} onSelectResult={openStudioSearchHit} isActive={isActive && activeStep === 'studio' && studioSearchMode === 'result'} />
                </> }}
              /> : <div className="playground-studio-detail-empty playground-studio-search-loading">
                <button type="button" className="studio-search-back-link" aria-label="Back to results" onClick={() => setStudioSearchMode('results')}><ArrowLeft size={14} aria-hidden="true" />Results</button>
                <span>{studioSearchVideoError || 'Loading selected result…'}</span>
                {studioSearchVideoError && studioSearchHit && <button type="button" onClick={() => openStudioSearchHit(studioSearchHit)}>Retry</button>}
              </div>}
            </div>}
            {activeStudioVideo ? (
              <VideoDetailModal
                video={studioDetailIsOpen ? activeStudioVideo : null}
                isOpen={studioDetailIsOpen}
                isVisible={studioDetailIsVisible}
                presentation="embedded"
                playbackMode="continuous-video"
                indexId="playground"
                deepLinkRunId={studioRunId}
                resultLink={resultLink}
                onResultLinkChange={(patch) => {
                  historyModeRef.current = 'push';
                  setResultLink((current) => ({
                    videoId: activeStudioVideoId!, runId: studioRunId, indexId: null,
                    segmentId: null, output: null, playback: null, metadata: null, resultScope: null, time: null, field: null,
                    ...current, ...patch,
                  }));
                }}
                deepLinkTime={resultLink?.time ?? (studioRestorePlaybackTime > 0 ? studioRestorePlaybackTime : null)}
                deepLinkFieldKey={resultLink?.field}
                onPlaybackTimeChange={handleStudioPlaybackTimeChange}
                workspaceState={effectiveStudioWorkspaceState}
                onWorkspaceStateChange={updateStudioWorkspaceState}
                onRunSelectionChange={handleStudioRunSelectionChange}
                embeddedHeaderActionsTarget={studioHeaderActionsTarget}
                onClose={handleCloseStudio}
              />
            ) : playgroundVideos.length > 0 && studioSearchMode === 'studio' ? (
              <div className="playground-studio-detail-empty">
                <Film size={22} />
                <span>Choose a media item above to open its Studio workspace.</span>
              </div>
            ) : null}
          </div>
        </JourneySection>
      </div>
      <div ref={studioResultSpaceRef} className="playground-scroll-space" aria-hidden="true" />
    </div>
    </PlaygroundFeatureAccessProvider>
  );
};

export default PlaygroundTab;
